import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { PasswordInput } from "../ui/password-input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, Trash, ShieldCheck } from "@phosphor-icons/react";
import { dt } from "../lux/format";
import { toast } from "sonner";

// SALES-036: B squad first claim. Admin only. The API key is write-only: the
// server never sends it back, so the field only ever shows its last 4.
const when = (s) => (s ? dt(s, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "never");
const pretty = (p) => { const d = String(p || "").replace(/\D/g, "").slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p; };
const mins = (n) => `${n} ${Number(n) === 1 ? "minute" : "minutes"}`;   // SALES-039
const TEST_TIMES = [["window_minutes", "Window", 1, 60], ["contact_minutes", "Contact within", 2, 60], ["warning_minutes", "Final warning", 1, 30]];

export default function LeadClaimSettings({ users }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ enabled: false, window_minutes: 30, contact_minutes: 15, warning_minutes: 5, members: [], managers: [] });
  const [stats, setStats] = useState([]);           // SALES-038: per member, last 30 days
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [test, setTest] = useState(null);          // SALES-037: the latest test run
  const [testPhones, setTestPhones] = useState("");
  const [testMembers, setTestMembers] = useState([]);   // B squad login ids to include
  const [testing, setTesting] = useState(false);
  const [testTimes, setTestTimes] = useState({ window_minutes: 5, contact_minutes: 3, warning_minutes: 1 });   // SALES-039
  const [testManagers, setTestManagers] = useState(false);   // SALES-040: off by default
  const hookUrl = `${window.location.origin}/api/public/quo/webhook`;

  const take = (data) => {
    setCfg(data);
    setForm({ enabled: data.enabled, window_minutes: data.window_minutes, contact_minutes: data.contact_minutes, warning_minutes: data.warning_minutes,
      members: data.members.map((m) => ({ ...m, phone: m.phone.replace(/^\+1/, "") })),
      managers: (data.managers || []).map((m) => ({ ...m, phone: m.phone.replace(/^\+1/, "") })) });
  };
  useEffect(() => {
    api.get("/lead-claim/settings").then((r) => take(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/lead-claim/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/lead-claim/test").then((r) => { setTest(r.data.run); setTestPhones(r.data.last_test_phones.join(", ")); if (r.data.timings) setTestTimes(r.data.timings); }).catch(() => {});
  }, []);
  // While a test waits for a reply or a CALLED, check every 3 seconds.
  const testLive = test?.status === "open" || test?.deadline?.state === "pending";
  useEffect(() => {
    if (!testLive) return undefined;
    const t = setInterval(() => api.get("/lead-claim/test").then((r) => setTest(r.data.run)).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [testLive, test?.no]);
  // SALES-039: keep "Last reply received" and "Last backup check" current without touching the form.
  useEffect(() => {
    const t = setInterval(() => api.get("/lead-claim/settings").then((r) => setCfg((c) => (c ? {
      ...c, last_webhook_at: r.data.last_webhook_at, last_poll_at: r.data.last_poll_at, last_error: r.data.last_error,
      send_error: r.data.send_error, send_error_at: r.data.send_error_at } : c))).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, []);

  const setMember = (i, patch) => setForm((f) => ({ ...f, members: f.members.map((m, j) => (j === i ? { ...m, ...patch } : m)) }));
  const addMember = () => setForm((f) => ({ ...f, members: [...f.members, { user_id: "", name: "", phone: "" }] }));
  const removeMember = (i) => setForm((f) => ({ ...f, members: f.members.filter((_, j) => j !== i) }));
  // SALES-040: managers are texted who claimed what. A name and a mobile; no CRM login needed.
  const setManager = (i, patch) => setForm((f) => ({ ...f, managers: f.managers.map((m, j) => (j === i ? { ...m, ...patch } : m)) }));
  const addManager = () => setForm((f) => ({ ...f, managers: [...f.managers, { name: "", phone: "" }] }));
  const removeManager = (i) => setForm((f) => ({ ...f, managers: f.managers.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { enabled: form.enabled, window_minutes: Number(form.window_minutes) || 30, members: form.members, managers: form.managers,
        contact_minutes: Number(form.contact_minutes) || 15, warning_minutes: Number(form.warning_minutes) || 5 };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      const { data } = await api.put("/lead-claim/settings", body);
      take(data);
      setApiKey("");
      toast.success("B squad first claim saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/lead-claim/connect", { url: hookUrl });
      take(data);
      toast.success("Quo will now tell the CRM when B squad replies");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConnecting(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/lead-claim/test", {
        phones: testPhones.split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean), member_ids: testMembers,
        window_minutes: Number(testTimes.window_minutes) || 5, contact_minutes: Number(testTimes.contact_minutes) || 3,
        warning_minutes: Number(testTimes.warning_minutes) || 1, managers: testManagers,
      });
      setTest(data.run);
      if (data.run.status === "failed") toast.error("Quo did not send the test. See below.");
      else toast.success(`Test #T${data.run.no} sent`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  if (!cfg) return null;
  const people = users.filter((u) => u.role !== "investor");
  const contact = Number(form.contact_minutes) || 15;
  const remind = Math.max(contact - 5, contact / 2);
  return (
    <div className="border border-border bg-card" data-testid="lead-claim-card">
      <div className="px-8 py-6 border-b border-border">
        <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /><div className="label-mono">Admin</div></div>
        <h2 className="font-heading text-2xl mt-1">B squad first claim</h2>
        <p className="text-xs text-muted-foreground mt-1">
          New leads with no owner are texted to these people from the 01 Motors Quo line, {pretty(cfg.line_number)}. The first to reply CLAIM gets the lead. When the window ends, the lead goes to the Leads page for everyone.
        </p>
      </div>

      <div className="px-8 py-6 space-y-8">
        <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
          <div>
            <Label className="label-mono mb-1.5 block">Texting</Label>
            <div className="flex items-center gap-3 h-10">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: !!v })} disabled={cfg.preview_site} data-testid="lead-claim-enabled" aria-label="Text new leads to B squad" />
              <span className="text-sm">{form.enabled ? "On" : "Off"}</span>
              {cfg.enabled && cfg.enabled_at ? <span className="text-xs text-muted-foreground">since {when(cfg.enabled_at)}</span> : null}
            </div>
          </div>
          <div>
            <Label className="label-mono mb-1.5 block" htmlFor="lead-claim-window">Window (minutes)</Label>
            <Input id="lead-claim-window" type="number" min={5} max={240} value={form.window_minutes}
              onChange={(e) => setForm({ ...form, window_minutes: e.target.value })} className="bg-input border-border w-32" data-testid="lead-claim-window" />
          </div>
          <div>
            <Label className="label-mono mb-1.5 block" htmlFor="lead-claim-contact">Mark contacted within (min)</Label>
            <Input id="lead-claim-contact" type="number" min={5} max={120} value={form.contact_minutes}
              onChange={(e) => setForm({ ...form, contact_minutes: e.target.value })} className="bg-input border-border w-32" data-testid="lead-claim-contact" />
          </div>
          <div>
            <Label className="label-mono mb-1.5 block" htmlFor="lead-claim-warning">Final warning (min)</Label>
            <Input id="lead-claim-warning" type="number" min={1} max={30} value={form.warning_minutes}
              onChange={(e) => setForm({ ...form, warning_minutes: e.target.value })} className="bg-input border-border w-32" data-testid="lead-claim-warning" />
          </div>
          <p className="text-xs text-muted-foreground basis-full -mt-1">
            After claiming, a member confirms any time before the lead is released, by texting CALLED or by marking the lead Contacted.
            {` They get a reminder ${mins(remind)} after claiming, a final warning at ${mins(contact)}, and ${mins(Number(form.warning_minutes) || 5)} later the lead goes back to the rest of B squad (a second time, to the Leads page). PASS gives it back at once.`}
          </p>
          {cfg.preview_site ? (
            <p className="text-xs text-amber-400 basis-full -mt-2" data-testid="lead-claim-preview-note">
              This is the preview site, so leads here are never texted to B squad. Use a test run; switch texting on in the live CRM.
            </p>
          ) : null}
        </div>

        <div>
          <Label className="label-mono mb-2 block">B squad</Label>
          <div className="space-y-3">
            {form.members.map((m, i) => (
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,16rem)_minmax(0,12rem)_auto] gap-3 items-center" data-testid={`lead-claim-member-${i}`}>
                <Select value={m.user_id || undefined} onValueChange={(v) => setMember(i, { user_id: v, name: people.find((u) => u.id === v)?.salesperson || people.find((u) => u.id === v)?.name || "" })}>
                  <SelectTrigger className="bg-input border-border" aria-label="CRM login" data-testid={`lead-claim-member-${i}-login`}><SelectValue placeholder="Pick a login" /></SelectTrigger>
                  <SelectContent>{people.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="tel" inputMode="tel" placeholder="Mobile, 10 digits" value={m.phone} aria-label="Mobile number"
                  onChange={(e) => setMember(i, { phone: e.target.value })} className="bg-input border-border col-start-1 sm:col-start-auto" data-testid={`lead-claim-member-${i}-phone`} />
                <Button variant="ghost" size="icon" onClick={() => removeMember(i)} aria-label="Remove" className="row-start-1 col-start-2 sm:row-start-auto sm:col-start-auto" data-testid={`lead-claim-member-${i}-remove`}>
                  <Trash size={16} className="text-destructive" />
                </Button>
              </div>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={addMember} className="mt-3" data-testid="lead-claim-add-member"><Plus size={14} className="mr-1" /> Add person</Button>
          {stats.length ? (
            <div className="mt-5 overflow-x-auto" data-testid="lead-claim-stats">
              <div className="label-mono mb-2">Last 30 days</div>
              <table className="text-sm tnum">
                <thead>
                  <tr className="text-xs text-muted-foreground text-left">
                    <th className="font-normal pr-4 sm:pr-6 pb-1.5">Member</th>
                    {["Claimed", "In time", "Passed", "Dropped", "Won"].map((h) => <th key={h} className="font-normal pr-4 sm:pr-6 pb-1.5 text-right whitespace-nowrap">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {stats.map((r) => (
                    <tr key={r.user_id} className="border-t border-border" data-testid={`lead-claim-stats-${r.user_id}`}>
                      <td className="pr-4 sm:pr-6 py-1.5 whitespace-nowrap">{r.name}</td>
                      <td className="pr-4 sm:pr-6 py-1.5 text-right whitespace-nowrap" title={r.waiting ? `${r.waiting} waiting for a call now` : undefined}>{r.claimed}{r.waiting ? <span className="text-xs text-muted-foreground"> ({r.waiting} now)</span> : null}</td>
                      <td className="pr-4 sm:pr-6 py-1.5 text-right">{r.in_time}</td>
                      <td className="pr-4 sm:pr-6 py-1.5 text-right">{r.passed}</td>
                      <td className={`pr-4 sm:pr-6 py-1.5 text-right ${r.dropped ? "text-amber-400" : ""}`}>{r.dropped}</td>
                      <td className="pr-4 sm:pr-6 py-1.5 text-right">{r.won}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-muted-foreground mt-2">In time: confirmed before the lead was released. (1 now): waiting for a call right now. Dropped: released after the warning.</p>
            </div>
          ) : null}
        </div>

        <div data-testid="lead-claim-managers">
          <Label className="label-mono mb-1 block">Managers</Label>
          <p className="text-xs text-muted-foreground mb-3 max-w-xl">
            Texted when a lead is claimed (who, and where the lead came from), when it goes back, and when nobody claims it. No customer details.
          </p>
          <div className="space-y-3">
            {form.managers.map((m, i) => (
              <div key={i} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,16rem)_minmax(0,12rem)_auto] gap-3 items-center" data-testid={`lead-claim-manager-${i}`}>
                <Input placeholder="Name" value={m.name} aria-label="Manager name" maxLength={40}
                  onChange={(e) => setManager(i, { name: e.target.value })} className="bg-input border-border" data-testid={`lead-claim-manager-${i}-name`} />
                <Input type="tel" inputMode="tel" placeholder="Mobile, 10 digits" value={m.phone} aria-label="Manager mobile number"
                  onChange={(e) => setManager(i, { phone: e.target.value })} className="bg-input border-border col-start-1 sm:col-start-auto" data-testid={`lead-claim-manager-${i}-phone`} />
                <Button variant="ghost" size="icon" onClick={() => removeManager(i)} aria-label="Remove manager" className="row-start-1 col-start-2 sm:row-start-auto sm:col-start-auto" data-testid={`lead-claim-manager-${i}-remove`}>
                  <Trash size={16} className="text-destructive" />
                </Button>
              </div>
            ))}
          </div>
          {form.managers.length < 5 ? (
            <Button variant="outline" size="sm" onClick={addManager} className="mt-3" data-testid="lead-claim-add-manager"><Plus size={14} className="mr-1" /> Add manager</Button>
          ) : null}
        </div>

        <div className="space-y-4">
          <div className="max-w-md">
            <Label className="label-mono mb-1.5 block" htmlFor="lead-claim-key">Quo API key</Label>
            <PasswordInput id="lead-claim-key" value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="off"
              placeholder={cfg.api_key_set ? `Saved (ends in ${cfg.api_key_last4})` : "Paste the key from Quo settings"}
              className="bg-input border-border" data-testid="lead-claim-api-key" />
            {cfg.api_key_set ? <p className="text-xs text-muted-foreground mt-1.5">To replace it, paste a new key and save.</p> : null}
          </div>
          <div>
            <Label className="label-mono mb-1.5 block">Replies from Quo</Label>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`text-sm ${cfg.webhook_connected ? "text-emerald-400" : "text-amber-400"}`} data-testid="lead-claim-webhook-status">
                {cfg.webhook_connected ? "Connected" : "Not connected"}
              </span>
              <Button variant="outline" size="sm" onClick={connect} disabled={connecting || !cfg.api_key_set} data-testid="lead-claim-connect">
                {connecting ? "Connecting…" : cfg.webhook_connected ? "Reconnect" : "Connect"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 break-all">Quo will send replies to {hookUrl}</p>
            <p className="text-xs text-muted-foreground mt-1">Last reply received: {when(cfg.last_webhook_at)}</p>
            <p className="text-xs text-muted-foreground">Last backup check: {when(cfg.last_poll_at)}</p>
            {cfg.last_error ? (
              <p className="text-xs text-destructive mt-1" data-testid="lead-claim-error">
                The last check with Quo did not go through. It tries again every minute. If this stays, check the API key and the Quo credit balance.
              </p>
            ) : null}
            {cfg.send_error ? (
              <p className="text-xs text-destructive mt-1" data-testid="lead-claim-send-error">
                Texts to B squad are failing since {when(cfg.send_error_at)}.{" "}
                {/402|Not Enough Credits/i.test(cfg.send_error)
                  ? "Quo is out of prepaid credits. Top up in Quo and new leads will be texted again."
                  : `Quo said: ${String(cfg.send_error).slice(0, 120)}`}
              </p>
            ) : null}
          </div>
          <div data-testid="lead-claim-test">
            <Label className="label-mono mb-1.5 block" htmlFor="lead-claim-test-phones">Test run</Label>
            <p className="text-xs text-muted-foreground mb-2 max-w-xl">
              Texts a sample lead to the numbers you type and the B squad members you tick, and creates no lead. It runs the whole flow on the test times below: the first to reply CLAIM gets the sample details, then gets the reminder and final warning, and the test goes to the others unless they reply CALLED in time. Works with texting on or off.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Input id="lead-claim-test-phones" value={testPhones} onChange={(e) => setTestPhones(e.target.value)} placeholder="Mobile numbers, separated by commas"
                className="bg-input border-border w-full sm:w-96" data-testid="lead-claim-test-phones" />
              <Button variant="outline" size="sm" onClick={sendTest} disabled={testing || !cfg.webhook_connected} data-testid="lead-claim-test-send"
                title={cfg.webhook_connected ? "" : "Connect first, so replies come back to the CRM"}>
                {testing ? "Sending…" : "Send test"}
              </Button>
            </div>
            {cfg.members.length ? (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-3" data-testid="lead-claim-test-members">
                <span className="text-xs text-muted-foreground">Also send to:</span>
                {cfg.members.map((m) => (
                  <label key={m.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={testMembers.includes(m.user_id)} aria-label={m.name}
                      onCheckedChange={(v) => setTestMembers((cur) => (v ? [...cur, m.user_id] : cur.filter((x) => x !== m.user_id)))} />
                    {m.name}
                  </label>
                ))}
                <button type="button" className="text-xs text-primary hover:underline" data-testid="lead-claim-test-all"
                  onClick={() => setTestMembers(testMembers.length === cfg.members.length ? [] : cfg.members.map((m) => m.user_id))}>
                  {testMembers.length === cfg.members.length ? "Clear" : "All of B squad"}
                </button>
              </div>
            ) : null}
            {cfg.managers?.length ? (
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-3" data-testid="lead-claim-test-managers">
                <Checkbox checked={testManagers} onCheckedChange={(v) => setTestManagers(!!v)} aria-label="Also text managers" />
                Also text managers <span className="text-xs text-muted-foreground">({cfg.managers.map((m) => m.name).join(", ")})</span>
              </label>
            ) : null}
            <div className="grid grid-cols-3 items-end gap-3 mt-3 max-w-md" data-testid="lead-claim-test-times">
              {TEST_TIMES.map(([k, label, lo, hi]) => (
                <div key={k}>
                  <Label className="text-xs text-muted-foreground mb-1 block leading-tight" htmlFor={`lead-claim-test-${k}`}>{label} (min)</Label>
                  <Input id={`lead-claim-test-${k}`} type="number" min={lo} max={hi} value={testTimes[k]}
                    onChange={(e) => setTestTimes((cur) => ({ ...cur, [k]: e.target.value }))} className="bg-input border-border w-full" data-testid={`lead-claim-test-${k}`} />
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">Test times are for test runs only. Real leads use the times above.</p>
            {!cfg.webhook_connected ? <p className="text-xs text-muted-foreground mt-1.5">Save the key and press Connect first.</p> : null}
            {test ? <TestStatus t={test} /> : null}
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="lead-claim-save">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

// SALES-037: what happened to the latest test run, in plain words.
function TestStatus({ t }) {
  const at = (x) => (x ? dt(x, { hour: "numeric", minute: "2-digit" }) : "");
  const atUp = (x) => (x ? at(new Date(Math.ceil(new Date(x).getTime() / 60000) * 60000).toISOString()) : "");   // as the texts show it
  const d = t.deadline;
  const sentOk = t.sent.filter((x) => x.ok).map((x) => x.phone);
  const failed = t.sent.filter((x) => !x.ok);
  const others = t.replies.filter((r) => r.result !== "won");
  return (
    <div className="mt-3 border border-border p-3 text-xs space-y-1 max-w-xl" data-testid="lead-claim-test-status">
      <p className="text-foreground">Test #T{t.no}{sentOk.length ? ` sent to ${sentOk.join(", ")} (${at(t.created_at)})` : ""}</p>
      {failed.map((x) => (
        <p key={x.phone} className="text-destructive">Not sent to {x.phone}: {x.error}. Check the number and the Quo credit balance.</p>
      ))}
      {(t.history || []).filter((h) => h.outcome !== "confirmed").map((h) => (
        <p key={h.outcome + h.at} className="text-muted-foreground">
          {h.outcome === "passed" ? `${h.name} passed (${at(h.at)}).` : `${h.name} did not reply CALLED in time (${at(h.at)}).`}
          {h.round === 1 && t.round === 2 ? " It went to the others." : " It went to the Leads page (in a real lead)."}
        </p>
      ))}
      {t.resent?.length ? <p className="text-muted-foreground">Round 2 sent to {t.resent.filter((x) => x.ok).map((x) => x.phone).join(", ")}</p> : null}
      {t.status === "open" ? <p className="text-amber-400">Waiting for a reply (CLAIM T{t.no}) until {at(t.expires_at)}</p> : null}
      {t.status === "claimed" ? (
        <p className="text-emerald-400">
          {t.winner} claimed it ({at(t.claimed_at)}). {t.details_ok === false ? "The details text did not send." : "Details text sent."}
        </p>
      ) : null}
      {d?.state === "pending" ? (
        <p className="text-amber-400" data-testid="lead-claim-test-deadline">
          {d.name} has until {atUp(d.warn_at)} to reply CALLED T{t.no} (reminder {atUp(d.remind_at)}, final warning {atUp(d.warn_at)}, released {atUp(d.release_at)})
        </p>
      ) : null}
      {(t.history || []).filter((h) => h.outcome === "confirmed").map((h) => (
        <p key={h.outcome + h.at} className="text-emerald-400">{h.name} replied CALLED ({at(h.at)}). Test complete.</p>
      ))}
      {others.map((r) => <p key={r.phone + r.at} className="text-muted-foreground">{r.phone} replied ({at(r.at)}) and was told it was taken.</p>)}
      {t.status === "expired" ? <p className="text-muted-foreground">No reply within {mins(t.window_minutes)}. If you did reply, Quo is not reaching the CRM: press Reconnect.</p> : null}
      {t.status === "failed" ? <p className="text-destructive">Nothing was sent.</p> : null}
      {t.managers ? <p className="text-muted-foreground">Managers are texted for this test.</p> : null}
      {t.offer_text ? <p className="text-muted-foreground pt-1">Text: {t.offer_text}</p> : null}
    </div>
  );
}
